import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ShoppingCart, ShoppingBag, Check, Clock4, BatteryCharging, Mountain, Layers, Sun, Snowflake, Leaf, CalendarRange, HelpCircle, MapPin, Wifi, Truck, Shield, Package, Wrench, Users } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { getProductBySlug } from '../data/products';
import { useCart } from '../context/CartContext';
export function Home() {
  const {
    addToCart
  } = useCart();
  // Bestsellers (exact spec order)
  const bestsellerSlugs = ['yarbo-lawn-mower', 'yarbo-lawn-mower-pro', 'yarbo-snow-blower', 'yarbo-blower', 'yarbo-all-season-pro-bundle', 'yarbo-all-season-bundle'];
  const bestsellers = bestsellerSlugs.map(slug => getProductBySlug(slug)).filter((p): p is NonNullable<typeof p> => Boolean(p));
  const bestsellerBullets: Record<string, string[]> = {
    'yarbo-lawn-mower': ['Up to 6 acres twice per week', 'Wire-free virtual boundaries', 'Auto-recharge and resume'],
    'yarbo-lawn-mower-pro': ['Dual 300W motors', 'Up to 2500W peak power', 'Built for thick, wet, or demanding lawns'],
    'yarbo-snow-blower': ['2-stage snow clearing', '24-inch clearing width', 'Clears up to 12 inches of snow'],
    'yarbo-blower': ['21N blowing force', '70-minute runtime per charge', 'Manage leaves and outdoor debris'],
    'yarbo-all-season-pro-bundle': ['All-season robotic yard care', 'Mowing, snow, and leaf cleanup', 'Best premium bundle'],
    'yarbo-all-season-bundle': ['Complete multi-season system', 'Mow, clear snow, and blow leaves', 'Strong all-season value']
  };
  const heroFeatured = getProductBySlug('yarbo-all-season-pro-bundle');
  const bundleOrder: {
    slug: string;
    bestFor: string;
  }[] = [{
    slug: 'yarbo-lawn-mower-snow-blower',
    bestFor: 'Mowing + snow'
  }, {
    slug: 'yarbo-lawn-mower-blower',
    bestFor: 'Mowing + leaves'
  }, {
    slug: 'yarbo-lawn-mower-pro-snow-blower',
    bestFor: 'Pro mowing + snow'
  }, {
    slug: 'yarbo-lawn-mower-pro-blower',
    bestFor: 'Pro mowing + leaves'
  }, {
    slug: 'yarbo-all-season-bundle',
    bestFor: 'All-season value'
  }, {
    slug: 'yarbo-all-season-pro-bundle',
    bestFor: 'Premium all-season'
  }, {
    slug: 'yarbo-snow-blower-blower',
    bestFor: 'Snow + debris cleanup'
  }];
  const bundles = bundleOrder.map(b => ({
    product: getProductBySlug(b.slug),
    bestFor: b.bestFor
  })).filter((b): b is {
    product: NonNullable<ReturnType<typeof getProductBySlug>>;
    bestFor: string;
  } => Boolean(b.product));
  const accessorySlugs = ['snow-plow-blade', 'straight-blades-bolts', 'cutting-blades-40', 'cutting-disc-bolts', 'rtk-antenna', 'high-rtk-antenna-mount', 'back-brace-mount', 'shear-pins', 'track-grease', 'tow-hitch', 'core-cover'];
  const accessories = accessorySlugs.map(slug => getProductBySlug(slug)).filter((p): p is NonNullable<typeof p> => Boolean(p));
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px'
    },
    transition: {
      duration: 0.5
    }
  };
  const categoryCards = [{
    title: 'Robot Lawn Mowers',
    desc: 'For scheduled autonomous mowing, large lawns, slopes, and weekly lawn maintenance.',
    cta: 'Shop Lawn Mowers',
    link: '/category/lawn-mowers',
    image: 'https://www.yarbo.com/cdn/shop/files/Yarbo_Lawn_Mower_Pro.png?v=1777093280&width=1445',
    alt: 'Yarbo robot lawn mower for residential lawns',
    tag: 'Lawn Care'
  }, {
    title: 'Robotic Snow Blowers',
    desc: 'For driveways, paths, and winter snow clearing with a dedicated Yarbo snow system.',
    cta: 'Shop Snow Blowers',
    link: '/category/snow-blowers',
    image: 'https://www.yarbo.com/cdn/shop/files/Yarbo_Snow_Blower.png?v=1777093280&width=1445',
    alt: 'Yarbo robotic snow blower clearing a driveway',
    tag: 'Winter'
  }, {
    title: 'Leaf Blowers & Debris Cleanup',
    desc: 'For leaves, clippings, driveway debris, and seasonal outdoor cleanup.',
    cta: 'Shop Leaf Blowers',
    link: '/category/leaf-blowers',
    image: 'https://www.yarbo.com/cdn/shop/files/Yarbo_Blower.png?v=1777093280&width=1445',
    alt: 'Yarbo leaf blower robot for yard debris cleanup',
    tag: 'Fall'
  }, {
    title: 'Trimming & Edging',
    desc: 'For edge and detail trimming with the Yarbo Trimmer Module.',
    cta: 'Shop Trimming',
    link: '/category/modules',
    image: 'https://www.yarbo.com/cdn/shop/files/Yarbo_Trimmer_Package.png?v=1777093279&width=1445',
    alt: 'Yarbo Trimmer Module for edge trimming',
    tag: 'Detail'
  }, {
    title: 'All-Season Bundles',
    desc: 'Best for new buyers who want one complete Yarbo setup for mowing, snow, and cleanup.',
    cta: 'Shop Bundles',
    link: '/category/bundles',
    image: 'https://www.yarbo.com/cdn/shop/files/Yarbo_4-in-1_Yard_Robot_with_Lawn_Mower_Pro_Module.png?v=1777093281&width=1445',
    alt: 'Yarbo modular yard robot with mower, snow blower, and blower modules',
    tag: 'New Buyer',
    highlight: true
  }, {
    title: 'Modules & Accessories',
    desc: 'For existing Yarbo Core owners adding modules, replacement parts, blades, batteries, and accessories.',
    cta: 'Shop Modules & Accessories',
    link: '/category/modules',
    image: 'https://www.yarbo.com/cdn/shop/files/Smart_Assist_Module.png?v=1777093277&width=1445',
    alt: 'Yarbo modules and accessories',
    tag: 'Existing Owner'
  }];
  return <div className="w-full bg-white" data-id="element-398">
      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden bg-stone-100" data-id="element-399">
        {/* Full-bleed background image */}
        <div className="absolute inset-0" data-id="element-400">
          <img src="https://www.yarbo.com/cdn/shop/files/Frame_2036458698.jpg" alt="Yarbo robot lawn mower with current savings up to $1,000 on select Yarbo systems" className="w-full h-full object-cover object-center" data-id="element-401" />
          {/* Soft gradient on left side only - keeps right-side savings overlay clear */}
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/60 to-transparent lg:from-white/90 lg:via-white/40 lg:to-transparent" data-id="element-402" />
        </div>

        {/* Foreground content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-403">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center min-h-[520px] lg:min-h-[620px] py-14 lg:py-20" data-id="element-404">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="lg:col-span-7 relative z-10" data-id="element-405">
              <span className="inline-block text-[11px] font-extrabold tracking-[0.2em] uppercase text-yarbo-dark bg-yarbo-yellow px-3 py-1.5 rounded-full mb-5" data-id="element-406">
                Yarbo Robotic Yard Care Systems
              </span>
              <h1 className="text-[2.25rem] sm:text-5xl lg:text-[3.5rem] xl:text-[4rem] font-extrabold text-yarbo-dark leading-[1.05] mb-6 tracking-tight max-w-[520px]" data-id="element-407">
                Take Yard Work Off Your List
              </h1>
              <p className="text-base lg:text-lg text-gray-800 mb-5 leading-relaxed max-w-xl" data-id="element-408">
                Shop Yarbo robot lawn mowers, robotic snow blowers, leaf
                blowers, bundles, modules, and accessories from Merem Robotics.
              </p>

              <p className="inline-flex items-center text-sm font-semibold text-yarbo-dark bg-white/90 backdrop-blur-sm border border-yarbo-yellow/60 rounded-lg px-4 py-2.5 mb-7 max-w-xl shadow-sm" data-id="element-409">
                Now is a smart time to shop Yarbo — current savings are featured
                on select systems.
              </p>

              <div className="flex flex-col sm:flex-row flex-wrap gap-3" data-id="element-410">
                <Link to="/shop" className="inline-flex items-center justify-center bg-yarbo-dark text-white font-bold px-7 py-4 rounded-lg hover:bg-black transition-colors shadow-lg" data-id="element-411">
                  <ShoppingBag className="w-5 h-5 mr-2" data-id="element-412" /> Shop Yarbo Products
                </Link>
                <Link to="/compare" className="inline-flex items-center justify-center bg-white text-yarbo-dark border-2 border-yarbo-dark font-bold px-7 py-4 rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-413">
                  Compare Yarbo Systems
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section className="bg-gray-50 border-y border-gray-200" data-id="element-414">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5" data-id="element-415">
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-y-3 gap-x-6" data-id="element-416">
            {['In Stock Products', 'Nationwide Shipping', 'Complete Systems & Modules', 'Secure Online Checkout'].map(item => <li key={item} className="flex items-center justify-center sm:justify-start gap-2.5 text-sm font-semibold text-yarbo-dark" data-id="element-417">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-yarbo-green/15" data-id="element-418">
                  <Check className="w-3 h-3 text-yarbo-green" data-id="element-419" />
                </span>
                <span data-id="element-420">{item}</span>
              </li>)}
          </ul>
        </div>
      </section>

      {/* ============ WEEKEND BACK ============ */}
      <section className="py-12 lg:py-14 bg-white" data-id="element-421">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-422">
          <motion.div {...fadeIn} className="max-w-3xl mb-8 lg:mb-10" data-id="element-423">
            <h2 className="text-4xl md:text-5xl font-extrabold text-yarbo-dark mb-4 leading-[1.1] tracking-tight" data-id="element-424">
              Your Weekend Shouldn't Revolve Around Yard Work
            </h2>
            <p className="text-lg lg:text-xl text-gray-700 leading-relaxed" data-id="element-425">
              Yarbo helps reduce the repetitive outdoor chores that keep eating
              into your free time — mowing, snow clearing, leaf cleanup, and
              seasonal yard maintenance.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5" data-id="element-426">
            {[{
            icon: Sun,
            title: 'Weekly Mowing',
            desc: 'Set mowing areas and schedules so the lawn gets handled without planning your weekend around cutting the grass.',
            link: '/category/lawn-mowers'
          }, {
            icon: Snowflake,
            title: 'Snow Clearing',
            desc: 'Use a dedicated Yarbo snow system to simplify driveway and path clearing when winter weather hits.',
            link: '/category/snow-blowers'
          }, {
            icon: Leaf,
            title: 'Leaves & Debris',
            desc: 'Manage leaves, clippings, and outdoor debris without dragging out the blower again and again.',
            link: '/category/leaf-blowers'
          }, {
            icon: CalendarRange,
            title: 'Multi-Season Yard Care',
            desc: 'Choose bundles and modules so one Yarbo platform can help with more than one outdoor job.',
            link: '/category/bundles'
          }].map(({
            icon: Icon,
            title,
            desc,
            link
          }) => <Link key={title} to={link} className="group bg-white border border-gray-200 hover:border-yarbo-dark hover:shadow-md transition-all rounded-2xl p-6 flex flex-col" data-id="element-427">
                <div className="w-12 h-12 rounded-xl bg-yarbo-yellow flex items-center justify-center mb-4 group-hover:scale-105 transition-transform" data-id="element-428">
                  <Icon className="w-6 h-6 text-yarbo-dark" data-id="element-429" />
                </div>
                <h3 className="font-bold text-yarbo-dark mb-2 text-lg" data-id="element-430">
                  {title}
                </h3>
                <p className="text-[15px] text-gray-800 leading-relaxed" data-id="element-431">
                  {desc}
                </p>
              </Link>)}
          </div>

          <div className="mt-10 text-center" data-id="element-432">
            <Link to="/shop" className="inline-flex items-center bg-yarbo-dark text-white font-bold px-7 py-3.5 rounded-lg hover:bg-black transition-colors" data-id="element-433">
              <ShoppingBag className="w-5 h-5 mr-2" data-id="element-434" /> Shop Yarbo Systems
            </Link>
          </div>
        </div>
      </section>

      {/* ============ ONE MODULAR YARBO SYSTEM ============ */}
      <section className="py-14 lg:py-16 bg-gray-50 border-t border-gray-100" data-id="element-435">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-436">
          <motion.div {...fadeIn} className="max-w-3xl mb-8 lg:mb-10" data-id="element-437">
            <span className="text-xs font-extrabold tracking-[0.15em] uppercase text-yarbo-dark/70 mb-3 block" data-id="element-438">
              Why Yarbo
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3 leading-tight" data-id="element-439">
              One Modular Yarbo System. Multiple Outdoor Jobs.
            </h2>
            <p className="text-lg text-gray-700" data-id="element-440">
              Yarbo is more than a robot mower. It is a modular robotic
              yard-care platform built for homeowners who want fewer repetitive
              outdoor chores, more consistent maintenance, and one system that
              can grow with their property.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-5" data-id="element-441">
            {[{
            icon: Clock4,
            title: 'Mow on Schedule',
            desc: 'Set mowing areas and schedules so the lawn is handled without dragging out the mower every week.'
          }, {
            icon: BatteryCharging,
            title: 'Recharge & Resume',
            desc: 'Yarbo is designed to return to the dock, recharge, and continue the job, helping reduce battery-life concerns on larger properties.'
          }, {
            icon: Mountain,
            title: 'Built for Real Terrain',
            desc: 'Tracked mobility, slope handling, and smart navigation help Yarbo work beyond small, flat lawns.'
          }, {
            icon: Layers,
            title: 'Add Modules Later',
            desc: 'Start with a complete system or bundle, then add compatible Yarbo modules and accessories as your needs change.'
          }].map(({
            icon: Icon,
            title,
            desc
          }) => <motion.div key={title} {...fadeIn} className="bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all rounded-2xl p-6" data-id="element-442">
                <div className="w-11 h-11 rounded-xl bg-yarbo-dark flex items-center justify-center mb-4" data-id="element-443">
                  <Icon className="w-5 h-5 text-yarbo-yellow" data-id="element-444" />
                </div>
                <h3 className="font-bold text-yarbo-dark mb-2 text-lg" data-id="element-445">
                  {title}
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-446">{desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ============ SHOP BY OUTDOOR JOB ============ */}
      <section className="py-14 lg:py-16 bg-white" data-id="element-447">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-448">
          <motion.div {...fadeIn} className="mb-8 lg:mb-10 max-w-3xl" data-id="element-449">
            <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3" data-id="element-450">
              Shop Yarbo by Outdoor Job
            </h2>
            <p className="text-lg text-gray-700" data-id="element-451">
              Start with the job you want to automate, then choose a complete
              system, bundle, module, or accessory.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-id="element-452">
            {categoryCards.map((cat, idx) => <motion.div key={cat.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: idx * 0.04
          }} data-id="element-453">
                <Link to={cat.link} className={`group block rounded-2xl border overflow-hidden bg-white hover:shadow-xl transition-all h-full ${cat.highlight ? 'border-yarbo-yellow ring-1 ring-yarbo-yellow' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-454">
                  <div className="aspect-[5/3] p-6 flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 relative" data-id="element-455">
                    <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider text-gray-700 bg-white px-2 py-1 rounded" data-id="element-456">
                      {cat.tag}
                    </span>
                    <img src={cat.image} alt={cat.alt} className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500" data-id="element-457" />
                  </div>
                  <div className="p-5 sm:p-6" data-id="element-458">
                    <h3 className="text-lg font-bold text-yarbo-dark mb-2 group-hover:text-black transition-colors" data-id="element-459">
                      {cat.title}
                    </h3>
                    <p className="text-sm text-gray-700 mb-4 leading-relaxed" data-id="element-460">
                      {cat.desc}
                    </p>
                    <span className="inline-flex items-center text-sm font-bold text-yarbo-dark" data-id="element-461">
                      {cat.cta}{' '}
                      <ArrowRight className="ml-1.5 w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-462" />
                    </span>
                  </div>
                </Link>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ============ BESTSELLERS ============ */}
      <section className="py-14 lg:py-16 bg-gray-50" data-id="element-463">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-464">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-8 lg:mb-10 gap-4" data-id="element-465">
            <div className="max-w-2xl" data-id="element-466">
              <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3" data-id="element-467">
                Bestselling Yarbo Systems
              </h2>
              <p className="text-lg text-gray-700" data-id="element-468">
                Popular complete systems and bundles for homeowners who want
                automated mowing, snow clearing, and yard cleanup.
              </p>
            </div>
            <Link to="/shop" className="hidden md:inline-flex items-center font-bold text-yarbo-dark hover:text-black whitespace-nowrap" data-id="element-469">
              Shop All Products <ArrowRight className="ml-2 w-5 h-5" data-id="element-470" />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-id="element-471">
            {bestsellers.map((p, idx) => {
            const bullets = bestsellerBullets[p.slug] || p.features.slice(0, 3);
            return <motion.div key={p.id} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: idx * 0.04
            }} className="bg-white rounded-2xl border border-gray-200 overflow-hidden hover:shadow-xl hover:border-gray-300 transition-all flex flex-col h-full group" data-id="element-472">
                  <Link to={`/product/${p.slug}`} className="block relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden p-5 sm:p-6" data-id="element-473">
                    <img src={p.image} alt={`${p.name} - Yarbo robotic yard-care system`} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" loading="lazy" data-id="element-474" />
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-green-50 text-green-700 ring-1 ring-inset ring-green-200 text-[11px] font-bold rounded-full px-2 py-1" data-id="element-475">
                      <Check className="w-3 h-3" data-id="element-476" /> In Stock
                    </div>
                  </Link>
                  <div className="p-5 flex flex-col flex-grow" data-id="element-477">
                    <h3 className="text-base sm:text-lg font-bold text-yarbo-dark leading-tight mb-1 line-clamp-2" data-id="element-478">
                      <Link to={`/product/${p.slug}`} className="hover:text-black transition-colors" data-id="element-479">
                        {p.name}
                      </Link>
                    </h3>
                    <div className="text-xl sm:text-2xl font-extrabold text-yarbo-dark mb-3" data-id="element-480">
                      ${p.price.toLocaleString()}
                    </div>
                    <ul className="space-y-1.5 mb-4 text-sm text-gray-700 flex-grow" data-id="element-481">
                      {bullets.map((b, i) => <li key={i} className="flex items-start gap-2" data-id="element-482">
                          <Check className="w-3.5 h-3.5 text-yarbo-green mt-0.5 flex-shrink-0" data-id="element-483" />
                          <span data-id="element-484">{b}</span>
                        </li>)}
                    </ul>
                    <div className="mt-auto flex flex-col gap-2" data-id="element-485">
                      <button onClick={() => addToCart(p)} className="w-full bg-yarbo-dark text-white font-bold text-sm py-3 rounded-lg hover:bg-black transition-colors flex items-center justify-center gap-2" data-id="element-486">
                        <ShoppingCart className="w-4 h-4" data-id="element-487" /> Add to Cart
                      </button>
                      <Link to={`/product/${p.slug}`} className="w-full text-center text-sm font-semibold text-gray-700 hover:text-yarbo-dark py-1" data-id="element-488">
                        View Details →
                      </Link>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* ============ MAKES SENSE FOR REAL HOMES ============ */}
      <section className="py-14 lg:py-16 bg-white border-t border-gray-200" data-id="element-489">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-490">
          <motion.div {...fadeIn} className="max-w-3xl mb-8 lg:mb-10" data-id="element-491">
            <span className="text-xs font-extrabold tracking-[0.15em] uppercase text-yarbo-dark/70 mb-3 block" data-id="element-492">
              Practical Robotic Yard Care
            </span>
            <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3 leading-tight" data-id="element-493">
              Robotic Yard Care That Makes Sense for Real Homes
            </h2>
            <p className="text-lg text-gray-700" data-id="element-494">
              Buying a yard robot is a big decision. Yarbo is designed to answer
              the practical questions homeowners have about coverage, battery
              life, setup, terrain, and year-round usefulness.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" data-id="element-495">
            {[{
            icon: MapPin,
            q: 'Will it cover my property?',
            a: 'Yarbo Lawn Mower supports large properties with scheduled mowing, multi-zone management, and coverage up to 6 acres twice per week.'
          }, {
            icon: BatteryCharging,
            q: 'What about battery life?',
            a: 'Yarbo is built around auto-recharge and resume operation, so it can return to the dock, charge, and continue instead of depending on one single run.'
          }, {
            icon: Wifi,
            q: 'Do I need boundary wires?',
            a: 'Yarbo uses app-defined virtual boundaries, so setup does not rely on burying physical perimeter wire.'
          }, {
            icon: Mountain,
            q: 'Can it handle slopes?',
            a: 'Supported Yarbo products are designed for slope handling up to 35 degrees / 70%, helping it work on more than flat suburban lawns.'
          }, {
            icon: Layers,
            q: 'Is it only for mowing?',
            a: 'No. With the right system and modules, Yarbo can support mowing, snow removal, leaf cleanup, trimming, and accessories.'
          }, {
            icon: Users,
            q: 'Is this for new buyers or existing owners?',
            a: 'New buyers should start with complete systems or bundles. Existing Yarbo Core owners can shop compatible modules and accessories.'
          }].map((item, idx) => <motion.div key={item.q} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: idx * 0.04
          }} className="bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all rounded-2xl p-6" data-id="element-496">
                <div className="w-10 h-10 rounded-lg bg-yarbo-dark flex items-center justify-center mb-4" data-id="element-497">
                  <item.icon className="w-5 h-5 text-yarbo-yellow" data-id="element-498" />
                </div>
                <h3 className="font-bold text-yarbo-dark mb-2 text-base" data-id="element-499">
                  {item.q}
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-500">
                  {item.a}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ============ NEW TO YARBO / EXISTING OWNER ============ */}
      <section className="py-14 lg:py-16 bg-gray-50" data-id="element-501">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-502">
          <motion.div {...fadeIn} className="text-center max-w-2xl mx-auto mb-8 lg:mb-10" data-id="element-503">
            <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3 leading-tight" data-id="element-504">
              New to Yarbo or Expanding a System?
            </h2>
            <p className="text-lg text-gray-700" data-id="element-505">
              Choose the path that matches where you are. Most first-time buyers
              should start with a complete system.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto" data-id="element-506">
            <motion.div {...fadeIn} className="relative bg-gradient-to-br from-yarbo-yellow/15 to-amber-50 border-2 border-yarbo-yellow rounded-2xl p-8 lg:p-10 shadow-lg" data-id="element-507">
              <div className="absolute top-5 right-5 text-[10px] font-bold uppercase tracking-wider text-yarbo-dark bg-yarbo-yellow px-2.5 py-1 rounded-full" data-id="element-508">
                Most Common
              </div>
              <div className="w-12 h-12 rounded-xl bg-yarbo-dark flex items-center justify-center mb-5" data-id="element-509">
                <Package className="w-6 h-6 text-yarbo-yellow" data-id="element-510" />
              </div>
              <h3 className="text-2xl font-extrabold text-yarbo-dark mb-3" data-id="element-511">
                New to Yarbo
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-512">
                Start with a complete system or bundle that includes the Yarbo
                Core and the module you need.
              </p>
              <Link to="/shop" className="inline-flex items-center bg-yarbo-dark text-white font-bold px-6 py-3.5 rounded-lg hover:bg-black transition-colors" data-id="element-513">
                Shop Complete Systems <ArrowRight className="ml-2 w-4 h-4" data-id="element-514" />
              </Link>
            </motion.div>

            <motion.div {...fadeIn} className="bg-white border-2 border-gray-200 hover:border-gray-300 rounded-2xl p-8 lg:p-10 transition-colors" data-id="element-515">
              <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-5" data-id="element-516">
                <Wrench className="w-6 h-6 text-yarbo-dark" data-id="element-517" />
              </div>
              <h3 className="text-2xl font-extrabold text-yarbo-dark mb-3" data-id="element-518">
                Already Own a Yarbo Core
              </h3>
              <p className="text-gray-700 mb-6 leading-relaxed" data-id="element-519">
                Add compatible modules, replacement parts, batteries, blades,
                and accessories to expand your Yarbo system.
              </p>
              <Link to="/category/modules" className="inline-flex items-center bg-white text-yarbo-dark border-2 border-yarbo-dark font-bold px-6 py-3.5 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-520">
                Shop Modules &amp; Accessories{' '}
                <ArrowRight className="ml-2 w-4 h-4" data-id="element-521" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ BUNDLES ============ */}
      <section className="py-14 lg:py-16 bg-white" data-id="element-522">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-523">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-8 lg:mb-10 gap-4" data-id="element-524">
            <div className="max-w-2xl" data-id="element-525">
              <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3" data-id="element-526">
                Bundle Yarbo for More Than One Outdoor Job
              </h2>
              <p className="text-lg text-gray-700" data-id="element-527">
                Choose a bundle when you want one robotic platform for mowing,
                snow clearing, leaf cleanup, or all-season yard care.
              </p>
            </div>
            <Link to="/category/bundles" className="hidden md:inline-flex items-center font-bold text-yarbo-dark hover:text-black whitespace-nowrap" data-id="element-528">
              View All Bundles <ArrowRight className="ml-2 w-5 h-5" data-id="element-529" />
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" data-id="element-530">
            {bundles.map(({
            product,
            bestFor
          }, idx) => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: idx * 0.04
          }} data-id="element-531">
                <ProductCard product={product} compact bestForLabel={bestFor} data-id="element-532" />
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ============ ACCESSORIES ============ */}
      <section className="py-14 lg:py-16 bg-gray-50" data-id="element-533">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-534">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-8 lg:mb-10 gap-4" data-id="element-535">
            <div className="max-w-2xl" data-id="element-536">
              <h2 className="text-2xl md:text-3xl font-extrabold text-yarbo-dark mb-2" data-id="element-537">
                Popular Yarbo Accessories &amp; Replacement Parts
              </h2>
              <p className="text-gray-700" data-id="element-538">
                Keep your Yarbo system ready with blades, mounts, snow
                accessories, batteries, and maintenance parts.
              </p>
            </div>
            <Link to="/category/accessories" className="hidden md:inline-flex items-center font-bold text-yarbo-dark hover:text-black whitespace-nowrap" data-id="element-539">
              View All Accessories <ArrowRight className="ml-2 w-5 h-5" data-id="element-540" />
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4" data-id="element-541">
            {accessories.map((acc, idx) => <motion.div key={acc.id} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: Math.min(idx * 0.03, 0.3)
          }} className="group bg-white border border-gray-200 hover:border-gray-300 rounded-xl p-3 sm:p-4 hover:shadow-md transition-all flex flex-col" data-id="element-542">
                <Link to={`/product/${acc.slug}`} className="block aspect-square bg-gray-50 rounded-lg p-3 mb-3 overflow-hidden" data-id="element-543">
                  <img src={acc.image} alt={`${acc.name} - Yarbo accessory`} className="w-full h-full object-contain group-hover:scale-105 transition-transform" data-id="element-544" />
                </Link>
                <Link to={`/product/${acc.slug}`} className="text-sm font-bold text-yarbo-dark hover:text-black line-clamp-2 mb-1 min-h-[2.5rem] leading-tight" data-id="element-545">
                  {acc.name}
                </Link>
                <div className="text-base font-extrabold text-yarbo-dark mb-2" data-id="element-546">
                  ${acc.price}
                </div>
                <button onClick={() => addToCart(acc)} className="mt-auto w-full bg-yarbo-dark hover:bg-black text-white font-bold text-xs py-2 rounded-lg transition-colors flex items-center justify-center gap-1.5" data-id="element-547">
                  <ShoppingCart className="w-3 h-3" data-id="element-548" /> Add to Cart
                </button>
              </motion.div>)}
          </div>

          <div className="mt-8 text-center md:hidden" data-id="element-549">
            <Link to="/category/accessories" className="inline-flex items-center font-bold text-yarbo-dark hover:text-black" data-id="element-550">
              View All Accessories <ArrowRight className="ml-2 w-4 h-4" data-id="element-551" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ COMPARE ============ */}
      <CompareSection data-id="element-552" />

      {/* ============ WHY MEREM ROBOTICS ============ */}
      <section className="py-14 lg:py-16 bg-[#111111]" data-id="element-553">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-554">
          <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-555">
            <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-3" data-id="element-556">
              Why Shop Yarbo From Merem Robotics?
            </h2>
            <p className="text-[#d1d5db] text-lg" data-id="element-557">
              A focused online store for Yarbo robotic yard care — complete
              systems, modules, bundles, and accessories.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" data-id="element-558">
            {[{
            icon: Package,
            title: 'Yarbo Product Selection',
            desc: 'Shop complete systems, bundles, modules, and accessories in one place.'
          }, {
            icon: Truck,
            title: 'Nationwide Shipping',
            desc: 'Merem Robotics is Michigan-based and sells Yarbo products online for customers nationwide.'
          }, {
            icon: Shield,
            title: 'Secure Online Checkout',
            desc: 'Clear product pages, visible pricing, cart buttons, and a checkout-focused shopping flow.'
          }, {
            icon: Package,
            title: 'Complete System Guidance',
            desc: 'New buyers can shop full systems and bundles instead of accidentally buying module-only products.'
          }, {
            icon: Wrench,
            title: 'Module & Accessory Organization',
            desc: 'Existing Yarbo Core owners can find compatible modules, blades, batteries, and maintenance parts.'
          }, {
            icon: ShoppingBag,
            title: 'Real Yarbo Product Images',
            desc: 'Product photography and product names match actual Yarbo systems and modules.'
          }].map(({
            icon: Icon,
            title,
            desc
          }) => <div key={title} className="bg-[#1f1f1f] border border-white/5 p-6 rounded-2xl" data-id="element-559">
                <div className="w-11 h-11 rounded-xl bg-yarbo-yellow/15 flex items-center justify-center mb-4" data-id="element-560">
                  <Icon className="w-5 h-5 text-yarbo-yellow" data-id="element-561" />
                </div>
                <h3 className="font-bold text-base mb-2 text-white" data-id="element-562">{title}</h3>
                <p className="text-sm text-[#cbd5e1] leading-relaxed" data-id="element-563">{desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-16 lg:py-20 bg-yarbo-yellow text-yarbo-dark text-center px-4" data-id="element-564">
        <div className="max-w-3xl mx-auto" data-id="element-565">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-5 leading-tight" data-id="element-566">
            Ready to Take Yard Work Off Your Weekly List?
          </h2>
          <p className="text-lg mb-8 opacity-90" data-id="element-567">
            Shop Yarbo robot lawn mowers, robotic snow blowers, leaf blowers,
            bundles, modules, and accessories online from Merem Robotics.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-568">
            <Link to="/shop" className="inline-flex items-center justify-center bg-yarbo-dark text-white font-bold px-8 py-4 rounded-lg hover:bg-black transition-colors" data-id="element-569">
              <ShoppingBag className="w-5 h-5 mr-2" data-id="element-570" /> Shop Yarbo Products
            </Link>
            <Link to="/compare" className="inline-flex items-center justify-center bg-white text-yarbo-dark font-bold px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-571">
              Compare Systems
            </Link>
            <Link to="/category/bundles" className="inline-flex items-center justify-center bg-transparent text-yarbo-dark border-2 border-yarbo-dark font-bold px-8 py-4 rounded-lg hover:bg-yarbo-dark/10 transition-colors" data-id="element-572">
              Shop Bundles
            </Link>
          </div>
        </div>
      </section>
    </div>;
}
// ============ Compare Section ============
function CompareSection() {
  const {
    addToCart
  } = useCart();
  const slugs = ['yarbo-lawn-mower', 'yarbo-lawn-mower-pro', 'yarbo-snow-blower', 'yarbo-blower', 'yarbo-all-season-pro-bundle'];
  const items = slugs.map(slug => getProductBySlug(slug)).filter((p): p is NonNullable<ReturnType<typeof getProductBySlug>> => Boolean(p));
  const rows = [{
    label: 'Best for',
    values: ['Standard mowing', 'Heavy/steep mowing', 'Winter snow clearing', 'Leaves & debris', 'Year-round automation']
  }, {
    label: 'Main task',
    values: ['Lawn care', 'Lawn care (Pro)', 'Snow removal', 'Leaf cleanup', 'All-season']
  }, {
    label: 'Property type',
    values: ['Large lawns', 'Demanding lawns', 'Driveways & paths', 'Wooded lots', 'Mixed property']
  }, {
    label: 'Auto-recharge',
    values: ['Yes', 'Yes', 'Yes', 'Yes', 'Yes']
  }, {
    label: 'Includes Core',
    values: ['Yes', 'Yes', 'Yes', 'Yes', 'Yes']
  }, {
    label: 'Best buyer type',
    values: ['Standard lawns', 'Demanding lawns', 'Winter homes', 'Wooded lots', 'New all-in-one buyer']
  }];
  return <>
      <section className="py-14 lg:py-16 bg-white border-t border-gray-200" data-id="element-573">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-574">
          <div className="text-center max-w-2xl mx-auto mb-10" data-id="element-575">
            <h2 className="text-3xl md:text-4xl font-extrabold text-yarbo-dark mb-3" data-id="element-576">
              Compare Yarbo Systems
            </h2>
            <p className="text-gray-700 text-lg" data-id="element-577">
              Choose based on the outdoor job you want to automate first.
            </p>
          </div>

          {/* Desktop */}
          <div className="hidden lg:block bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-578">
            <table className="w-full" data-id="element-579">
              <thead data-id="element-580">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-581">
                  <th className="p-5 text-left text-xs font-bold uppercase tracking-wider text-gray-700 w-1/6" data-id="element-582">
                    Feature
                  </th>
                  {items.map((p, i) => <th key={p.id} className={`p-5 text-center ${i === 4 ? 'bg-yarbo-yellow/10' : ''}`} data-id="element-583">
                      <div className="aspect-square w-24 mx-auto mb-3 bg-gray-50 rounded-lg p-2" data-id="element-584">
                        <img src={p.image} alt={`${p.name} comparison`} className="w-full h-full object-contain" data-id="element-585" />
                      </div>
                      <div className="text-sm font-bold text-yarbo-dark mb-1" data-id="element-586">
                        {p.name}
                      </div>
                      <div className="text-lg font-extrabold text-yarbo-dark mb-2" data-id="element-587">
                        ${p.price.toLocaleString()}
                      </div>
                      <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 ring-1 ring-green-200 text-[10px] font-bold rounded-full px-2 py-0.5" data-id="element-588">
                        <Check className="w-2.5 h-2.5" data-id="element-589" /> In Stock
                      </span>
                    </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-590">
                {rows.map((row, idx) => <tr key={idx} data-id="element-591">
                    <td className="p-5 font-bold text-gray-800 text-sm" data-id="element-592">
                      {row.label}
                    </td>
                    {row.values.map((val, i) => <td key={i} className={`p-5 text-center text-sm ${i === 4 ? 'bg-yarbo-yellow/5 font-medium text-yarbo-dark' : 'text-gray-700'}`} data-id="element-593">
                        {val}
                      </td>)}
                  </tr>)}
                <tr className="bg-gray-50" data-id="element-594">
                  <td className="p-5" data-id="element-595"></td>
                  {items.map((p, i) => <td key={p.id} className={`p-4 text-center ${i === 4 ? 'bg-yarbo-yellow/10' : ''}`} data-id="element-596">
                      <button onClick={() => addToCart(p)} className="w-full bg-yarbo-dark text-white font-bold text-sm py-3 rounded-lg hover:bg-black transition-colors flex items-center justify-center gap-2" data-id="element-597">
                        <ShoppingCart className="w-4 h-4" data-id="element-598" /> Add to Cart
                      </button>
                      <Link to={`/product/${p.slug}`} className="block mt-2 text-xs font-bold text-gray-700 hover:text-yarbo-dark" data-id="element-599">
                        View Details →
                      </Link>
                    </td>)}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile stacked */}
          <div className="lg:hidden space-y-4" data-id="element-600">
            {items.map((p, i) => <div key={p.id} className={`bg-white rounded-xl border p-5 ${i === 4 ? 'border-yarbo-yellow bg-yarbo-yellow/5' : 'border-gray-200'}`} data-id="element-601">
                <div className="flex gap-4 mb-4" data-id="element-602">
                  <div className="w-20 h-20 bg-gray-50 rounded-lg p-2 flex-shrink-0" data-id="element-603">
                    <img src={p.image} alt={p.name} className="w-full h-full object-contain" data-id="element-604" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-605">
                    <h3 className="font-bold text-yarbo-dark mb-1 leading-tight" data-id="element-606">
                      {p.name}
                    </h3>
                    <div className="text-xl font-extrabold text-yarbo-dark mb-2" data-id="element-607">
                      ${p.price.toLocaleString()}
                    </div>
                    <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 ring-1 ring-green-200 text-[10px] font-bold rounded-full px-2 py-0.5" data-id="element-608">
                      <Check className="w-2.5 h-2.5" data-id="element-609" /> In Stock
                    </span>
                  </div>
                </div>
                <dl className="space-y-1.5 text-sm mb-4" data-id="element-610">
                  {rows.map((row, idx) => <div key={idx} className="flex justify-between gap-3" data-id="element-611">
                      <dt className="text-gray-500" data-id="element-612">{row.label}</dt>
                      <dd className="text-gray-800 font-medium text-right" data-id="element-613">
                        {row.values[i]}
                      </dd>
                    </div>)}
                </dl>
                <div className="flex gap-2" data-id="element-614">
                  <button onClick={() => addToCart(p)} className="flex-1 bg-yarbo-dark text-white font-bold py-3 rounded-lg text-sm flex items-center justify-center gap-2" data-id="element-615">
                    <ShoppingCart className="w-4 h-4" data-id="element-616" /> Add to Cart
                  </button>
                  <Link to={`/product/${p.slug}`} className="flex-1 text-center text-yarbo-dark border border-gray-200 font-bold py-3 rounded-lg text-sm" data-id="element-617">
                    View Details
                  </Link>
                </div>
              </div>)}
          </div>
        </div>
      </section>
    </>;
}